/**
 * @file 发送性能测试上报
 * @author dafo<huanghouin@baijiahulian.com>
 * @module module/perf
 */
import isClient from './util/detectClient';
import request from './util/request';
import configurable from './util/configurable';
import performance from './util/performance';
import connection from './util/connection';
import {
    on,
    off
} from './util/dom';
import {
    trackID,
    getUserID,
    Config,
    HB_SESSION_ID_KEY
} from './util/config';
import delay from './util/delay';
import cookie from './util/cookie';

/**
 * 性能上报
 *
 * @function
 * @param {Object} json 要上报的数据
 * @param {Function=} callback 上报后的回调
 */
const report = configurable((json, callback) => {
    const {
        url = location.href,
        uuid = trackID,
        uid = getUserID(),
        path = location.pathname,
        ...rest
    } = json;

    // 要上报的数据
    const perfData = {
        // 当前页面 URL
        url,

        // 用户的唯一标识，由后端生成写到 cookie 中
        uuid,

        // 登录用户 ID，用户登录后端写到 cookie 中
        uid,

        // 定义的当前页面路径，以区分多个页面
        path,

        ...rest
    };

    request(Config.VERB)(`${Config.PERF}`, perfData, callback);
}, {});

const win = isClient && window;

/**
 * 解除相关事件的绑定
 */
function destroy() {
    // 在 load 或 unload 时上报
    // 哪个先触发就在哪里上报，只上报一次
    off(win, 'load', observe);
    off(win, 'unload', observe);
}


/**
 * 处理事件
 *
 * @return {Undefined}
 */
function observe() {

    const {timing} = performance;

    destroy();

    if (!timing) {
        return;
    }

    const now = +new Date();
    const {
        connectEnd,
        connectStart,
        domainLookupEnd,
        domainLookupStart,
        domContentLoadedEventEnd,
        domComplete,
        domLoading,
        loadEventEnd,
        navigationStart,
        requestStart,
        responseStart,
        responseEnd,

        // IE/Edage 中实现的首屏渲染时间
        msFirstPaint = now
    } = timing;

    let ttfp = msFirstPaint - navigationStart;
    if (!responseEnd || !loadEventEnd || ttfp < 0) {
        return delay(observe)();
    }

    const paints = performance.getEntriesByType ? performance.getEntriesByType('paint') : [];

    if (paints.length) {
        ttfp = paints[paints.length - 1].startTime | 0;
    }
    else if (win.chrome && win.chrome.loadTimes) {
        const loadedTimes = win.chrome.loadTimes();
        if (loadedTimes && loadedTimes.firstPaintTime && loadedTimes.startLoadTime) {
            ttfp = parseInt((loadedTimes.firstPaintTime - loadedTimes.startLoadTime) * 1000, 10);

            if (ttfp < 0) {
                return delay(observe)();
            }
        }
    }

    report({
        // DNS 解析时间
        dns: domainLookupEnd - domainLookupStart,

        // TCP 连接时间
        tcp: connectEnd - connectStart,

        // 响应时间
        resp: responseEnd - responseStart,

        // 页面可交互时间
        ready: domContentLoadedEventEnd - navigationStart,

        // DOM 树解析时间
        tree: domComplete - domLoading,

        // 接收第一个字节的时间
        ttfb: responseStart - requestStart,

        // 白屏时间
        ttsr: domLoading - navigationStart,

        // 首屏时间
        ttfp,

        // 页面加载完成时间
        ttdc: loadEventEnd - navigationStart,

        // 相关网络信息
        ...connection,

        // 请求开始发出的时间
        actionStart: requestStart,

        // action Name 页面路径
        actionName: location.origin + location.pathname,

        // 请求类型，页面加载
        reqType: 'page_load',

        // appid 项目在擎天云节点树上的标识
        appNid: Config.appNid || Config.appId || Config.APPNID,

        // 前端页面会话级session id
        [HB_SESSION_ID_KEY]: cookie.get(HB_SESSION_ID_KEY)
    });
}


on(win, 'load', observe);
on(win, 'unload', observe);

export default report;
